import { useEffect, useRef, useState } from "react";
import { tokens } from "./api";
import type { EventRow } from "../types/api";

const WS_BASE = import.meta.env.VITE_WS_BASE ?? "/ws";

function wsUrl(slug: string): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const base = WS_BASE.startsWith("http")
    ? WS_BASE.replace(/^http/, "ws")
    : `${proto}://${window.location.host}${WS_BASE}`;
  const token = tokens.access ?? "";
  return `${base}/exceptions/${slug}/?token=${encodeURIComponent(token)}`;
}

export interface FeedState {
  connected: boolean;
  live: EventRow[];
}

/**
 * Subscribe to a tenant's real-time exception feed. Returns the live events
 * (most-recent first, capped) and a connection indicator.
 */
export function useExceptionFeed(slug: string | undefined, cap = 40): FeedState {
  const [connected, setConnected] = useState(false);
  const [live, setLive] = useState<EventRow[]>([]);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!slug) return;
    let closed = false;
    let retry: ReturnType<typeof setTimeout>;

    function connect() {
      const ws = new WebSocket(wsUrl(slug!));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (!closed) retry = setTimeout(connect, 2500);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "exception" && msg.event) {
            setLive((prev) => {
              if (prev[0]?.id === msg.event.id) return prev;
              return [msg.event as EventRow, ...prev].slice(0, cap);
            });
          }
        } catch {
          /* ignore */
        }
      };
    }
    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      wsRef.current?.close();
    };
  }, [slug, cap]);

  return { connected, live };
}
